import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Navigate, useNavigate } from 'react-router-dom';
import { UsersRound, Calendar as CalendarIcon, PlusIcon, ChevronDownIcon, Search, MapPin, Building2, Clock, ArrowRight, X } from 'lucide-react';
import { AppSelect } from '../../components/common/select';
import Loading from '../../components/Loading';
import api from '../../api';
import { useAuth } from '../../contexts/AuthProvider';
import AddGeneralScheduleOffcanvas from './AddGeneralScheduleOffcanvas';
import { getGeneralSchedules } from './api/generalScheduleApi';
import './Calendar.css';

/** Matches NavLinks `canViewModule` / AuditOutputTemplate module key */
const CALENDAR_MODULE = 'calendar';
const DEFAULT_EVENT_TYPES = [
  { id: 'audit', label: 'Audit', color: '#b794f6', bg: '#e9e3f5' },
  { id: 'meeting', label: 'Meeting', color: '#ed8936', bg: '#fef3e2' },
  { id: 'auto-scheduling', label: 'Auto Scheduling', color: '#4299e1', bg: '#e6f2ff' },
  { id: 'sales-visit', label: 'Sales Visit', color: '#48bb78', bg: '#e6ffed' },
  { id: 'business-trip', label: 'Business Trip / Travel', color: '#38a169', bg: '#e0f2e9' },
  { id: 'annual-leave', label: 'Annual Leave', color: '#e53e3e', bg: '#ffe6e6' },
  { id: 'event', label: 'Event', color: '#3182ce', bg: '#e3f2fd' },
];

const ADD_SCHEDULE_OPTIONS = [
  { id: 'meeting', label: 'Meeting' },
  { id: 'sales-visit', label: 'Sales Visit' },
  { id: 'business-trip', label: 'Business Trip / Travel' },
  { id: 'event', label: 'Event' },
];

const VIEWS = ['Month', 'Week', 'Day', 'List'];

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const HOURS_PER_DAY = 24;
const SLOTS_PER_HOUR = 2; // 30-min slots
const TOTAL_SLOTS = HOURS_PER_DAY * SLOTS_PER_HOUR;

function slugifyCategory(value) {
  if (!value) return 'audit';
  const slug = String(value)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '');
  return slug || 'audit';
}

function normalizeCategory(raw, index = 0) {
  const id = slugifyCategory(raw?.id || raw?.label || `category-${index + 1}`);
  return {
    id,
    label: String(raw?.label || raw?.name || id).trim() || id,
    color: String(raw?.color || '#7c3aed'),
    bg: String(raw?.bg || '#f3e8ff'),
  };
}

function resolveScheduleCategoryId(row) {
  if (!row || typeof row !== 'object') return 'audit';
  const keys = ['categoryId', 'category', 'categoryType', 'scheduleCategory', 'eventCategory'];
  for (let i = 0; i < keys.length; i += 1) {
    const value = String(row[keys[i]] || '').trim();
    if (value) return slugifyCategory(value);
  }
  return 'audit';
}

function toDateOnly(value) {
  if (!value) return null;
  const d = new Date(`${String(value).slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  return d;
}

function formatDateKey(dateObj) {
  if (!(dateObj instanceof Date) || Number.isNaN(dateObj.getTime())) return '';
  const y = dateObj.getFullYear();
  const m = `${dateObj.getMonth() + 1}`.padStart(2, '0');
  const d = `${dateObj.getDate()}`.padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function buildDateRange(startValue, endValue) {
  const start = toDateOnly(startValue);
  const end = toDateOnly(endValue || startValue);
  if (!start || !end || start > end) return [];

  const out = [];
  const cursor = new Date(start.getTime());
  while (cursor <= end) {
    out.push(formatDateKey(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

// Get Sunday of the week for a given date
function getWeekStart(date) {
  const d = new Date(date);
  const day = d.getDay();
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d;
}

// Get the 7 dates of the week (Sun–Sat)
function getWeekDates(weekStart) {
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(weekStart);
    d.setDate(weekStart.getDate() + i);
    dates.push(d);
  }
  return dates;
}

function getCalendarDays(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDay = first.getDay();
  const daysInMonth = last.getDate();

  const prevMonth = month === 0 ? 11 : month - 1;
  const prevYear = month === 0 ? year - 1 : year;
  const prevLast = new Date(prevYear, prevMonth + 1, 0).getDate();

  const rows = [];
  let day = 1;
  let nextMonthDay = 1;

  for (let r = 0; r < 6; r++) {
    const week = [];
    for (let c = 0; c < 7; c++) {
      const cellIndex = r * 7 + c;
      if (cellIndex < startDay) {
        week.push({
          date: new Date(prevYear, prevMonth, prevLast - startDay + cellIndex + 1),
          isCurrentMonth: false,
        });
      } else if (day <= daysInMonth) {
        week.push({
          date: new Date(year, month, day),
          isCurrentMonth: true,
        });
        day++;
      } else {
        week.push({
          date: new Date(year, month + 1, nextMonthDay),
          isCurrentMonth: false,
        });
        nextMonthDay++;
      }
    }
    rows.push(week);
  }
  return rows;
}

function getActiveCalendarMonth() {
  const now = new Date();
  return { year: now.getFullYear(), month: now.getMonth() };
}

// Status order for display: confirmed first, then scheduled, then available
const AVAILABILITY_STATUS_ORDER = { confirmed: 0, scheduled: 1, available: 2 };

const GENERAL_TYPE_LABEL_MAP = {
  meeting: 'Meeting',
  'sales-visit': 'Sales Visit',
  'business-trip': 'Business Trip / Travel',
  event: 'Event',
};

function convertGeneralSchedulesToCalendarEvents(schedules, eventTypeMap) {
  const groupedByDate = new Map();
  schedules.forEach((schedule) => {
    const typeId = schedule.type || 'meeting';
    const dates = buildDateRange(
      String(schedule.startDate || '').slice(0, 10),
      String((schedule.endDate || schedule.startDate || '')).slice(0, 10)
    );
    dates.forEach((dateKey) => {
      const groupKey = `${dateKey}::${typeId}`;
      if (!groupedByDate.has(groupKey)) {
        groupedByDate.set(groupKey, { dateKey, typeId, items: [] });
      }
      groupedByDate.get(groupKey).items.push(schedule);
    });
  });
  return Array.from(groupedByDate.values())
    .map(({ dateKey, typeId, items }) => {
      const typeLabel =
        eventTypeMap?.[typeId]?.label || GENERAL_TYPE_LABEL_MAP[typeId] || typeId;
      return {
        id: `general-${dateKey}::${typeId}`,
        date: toDateOnly(dateKey),
        label: typeLabel,
        typeId,
        count: items.length,
        items: items.map((schedule, idx) => {
          const assigneeNames = Array.isArray(schedule.assignees)
            ? schedule.assignees
                .map((a) => a.fullName || a.name || `User #${a.userId}`)
                .join(', ')
            : '-';
          return {
            id: `general-${schedule.id}-${idx}`,
            _index: idx + 1,
            title: schedule.title || typeLabel,
            subtitle:
              schedule.companyName ||
              (Array.isArray(schedule.assignees) && schedule.assignees[0]?.fullName) ||
              '-',
            projectCode: '',
            projectId: null,
            scheduleId: schedule.id,
            rawSchedule: schedule,
            certification: `Assigned to: ${assigneeNames}`,
            location: schedule.location || null,
          };
        }),
      };
    })
    .filter((ev) => ev.date != null);
}

const Calendar = () => {
  const navigate = useNavigate();
  const { can, capabilitiesLoading } = useAuth();

  const canViewPage =
    can('view_own', CALENDAR_MODULE) ||
    can('view_department', CALENDAR_MODULE) ||
    can('view_global', CALENDAR_MODULE);
  const canExportCalendar = can('export_data', CALENDAR_MODULE);
  // Calendar schedules are operational availability records. Any user who can view
  // Calendar may create or reschedule them; Audit Scheduling remains Audit Process-only.
  const canManageCalendarSchedules = canViewPage;
  const canShowAddSchedule = canManageCalendarSchedules;

  const addScheduleOptionsVisible = ADD_SCHEDULE_OPTIONS;

  const [segmentTab, setSegmentTab] = useState('schedule'); // 'availability' | 'schedule'
  const [availabilitySelectedDate, setAvailabilitySelectedDate] = useState(() => new Date());
  const [availabilityCalendarMonth, setAvailabilityCalendarMonth] = useState(() => new Date());
  const [availabilitySearch, setAvailabilitySearch] = useState('');
  const [availabilityCountry, setAvailabilityCountry] = useState('All Country');
  const [availabilityCity, setAvailabilityCity] = useState('All City');
  const [availabilityStatus, setAvailabilityStatus] = useState('All Status');
  const [current, setCurrent] = useState(getActiveCalendarMonth);
  const [weekStart, setWeekStart] = useState(() => getWeekStart(new Date()));
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('Month');
  const [filterOpen, setFilterOpen] = useState(true);
  const [viewAll, setViewAll] = useState(true);
  const [calendarEventTypes, setCalendarEventTypes] = useState(DEFAULT_EVENT_TYPES);
  const [typeFilters, setTypeFilters] = useState(
    DEFAULT_EVENT_TYPES.reduce((acc, t) => ({ ...acc, [t.id]: true }), {})
  );
  const [addScheduleDropdownOpen, setAddScheduleDropdownOpen] = useState(false);
  const addScheduleRef = useRef(null);
  const [generalScheduleOpen, setGeneralScheduleOpen] = useState(false);
  const [generalScheduleType, setGeneralScheduleType] = useState('meeting');
  const [editGeneralScheduleData, setEditGeneralScheduleData] = useState(null);
  const [moreDropdown, setMoreDropdown] = useState({ key: null, anchor: null, dayLabel: '' });
  const moreDropdownRef = useRef(null);
  const [eventDetailModal, setEventDetailModal] = useState(null); // { event, date }
  const [auditCalendarEvents, setAuditCalendarEvents] = useState([]);
  const [generalCalendarEvents, setGeneralCalendarEvents] = useState([]);
  const [clickedDate, setClickedDate] = useState('');
  const [isCalendarLoading, setIsCalendarLoading] = useState(true);
  const [calendarLoadError, setCalendarLoadError] = useState('');
  const [availabilityAuditorPool, setAvailabilityAuditorPool] = useState([]);
  const [availabilityAssignments, setAvailabilityAssignments] = useState([]);
  const getEventDetailItems = (event) => {
    if (!event) return [];
    return Array.isArray(event.items) ? event.items : [];
  };

  const loadAuditCalendarEvents = useCallback(async () => {
    if (!canViewPage) return;
    setIsCalendarLoading(true);
    setCalendarLoadError('');
    try {
      const overviewRes = await api.get('/audit/calendar/overview');
      const payload = overviewRes?.data || {};
      const projects = Array.isArray(payload?.projects) ? payload.projects : [];
      const backendCategories = Array.isArray(payload?.categories) ? payload.categories : [];

      const normalizedTypes = backendCategories.length > 0
        ? backendCategories.map((row, idx) => normalizeCategory(row, idx))
        : DEFAULT_EVENT_TYPES;
      setCalendarEventTypes(normalizedTypes);

      const normalizedTypeMap = normalizedTypes.reduce((acc, type) => {
        acc[type.id] = type;
        return acc;
      }, {});
      const categoryIds = new Set(normalizedTypes.map((x) => x.id));

      const expandedRows = [];
      const auditorPoolMap = new Map();
      const assignments = [];
      const overviewAuditors = Array.isArray(payload?.auditors) ? payload.auditors : [];
      overviewAuditors.forEach((auditor) => {
        const userId = auditor?.userId != null ? String(auditor.userId) : null;
        const key = userId || `name:${String(auditor?.label || auditor?.fullName || auditor?.username || '').trim()}`;
        if (!key || key === 'name:') return;
        if (auditorPoolMap.has(key)) return;

        const competencyRows = Array.isArray(auditor?.competencies) ? auditor.competencies : [];
        const competency = competencyRows[0] || {};
        const roleText = competency?.qualificationName || competency?.qualificationLevel || 'Auditor';

        auditorPoolMap.set(key, {
          id: key,
          userId,
          name: String(auditor?.label || auditor?.fullName || auditor?.username || '-').trim() || '-',
          role: roleText,
        });
      });

      projects.forEach((projectRow) => {
        const projectId = Number(projectRow?.projectId);
        const project = {
          id: projectId,
          projectName: projectRow?.projectName,
          projectCode: projectRow?.projectCode,
          companyName: projectRow?.companyName,
        };
        const scheduleScopes = Array.isArray(projectRow?.schedules) ? projectRow.schedules : [];

        scheduleScopes.forEach((scopeRow) => {
          const siteLocations = Array.isArray(scopeRow?.siteLocations) ? scopeRow.siteLocations : [];
          const siteMap = new Map(
            siteLocations
              .map((site) => [String(site?.id || ''), site])
              .filter(([id]) => id)
          );
          const siteNameById = new Map(
            siteLocations
              .map((site) => [String(site?.id || ''), site?.siteName || site?.address || '-'])
              .filter(([id]) => id)
          );

          const schedules = Array.isArray(scopeRow?.auditSchedules) ? scopeRow.auditSchedules : [];
          schedules.forEach((schedule, index) => {
            const explicitDates = Array.isArray(schedule?.mandayDetails)
              ? schedule.mandayDetails
                  .map((row) => String(row?.date || '').slice(0, 10))
                  .filter(Boolean)
              : [];
            const dates = explicitDates.length > 0
              ? Array.from(new Set(explicitDates))
              : buildDateRange(schedule?.startDate, schedule?.endDate);

            if (dates.length === 0) return;

            const siteLocationNames = Array.isArray(schedule?.siteLocationDetails)
              ? schedule.siteLocationDetails
                  .flatMap((row) => Array.isArray(row?.siteLocationIds) ? row.siteLocationIds : [])
                  .map((siteId) => siteNameById.get(String(siteId)) || null)
                  .filter(Boolean)
              : [];

            const siteText = siteLocationNames.length > 0
              ? Array.from(new Set(siteLocationNames)).join(', ')
              : '-';

            const startDateText = schedule?.startDate ? String(schedule.startDate).slice(0, 10) : dates[0];
            const endDateText = schedule?.endDate ? String(schedule.endDate).slice(0, 10) : dates[dates.length - 1];
            const auditorName = (schedule?.employeeName || '').trim() || '-';
            const employeeId = schedule?.employeeId != null ? String(schedule.employeeId) : '';
            const scheduleStatusRaw = String(schedule?.scheduleStatus || '').trim();
            const normalizedStatus = scheduleStatusRaw.toLowerCase() === 'confirmed' ? 'confirmed' : 'scheduled';
            const roleText = String(schedule?.role || '').trim() || 'Auditor';
            const scheduleCategoryId = resolveScheduleCategoryId(schedule);
            const resolvedTypeId = (() => {
              if (categoryIds.has(scheduleCategoryId)) return scheduleCategoryId;
              return categoryIds.has('audit') ? 'audit' : scheduleCategoryId;
            })();
            const resolvedTypeLabel = normalizedTypeMap[resolvedTypeId]?.label || 'Audit';

            dates.forEach((dateKey, dateIndex) => {
              const sameDateSiteIds = Array.isArray(schedule?.siteLocationDetails)
                ? schedule.siteLocationDetails
                    .filter((row) => String(row?.date || '').slice(0, 10) === dateKey)
                    .flatMap((row) => Array.isArray(row?.siteLocationIds) ? row.siteLocationIds : [])
                : [];
              const fallbackSiteIds = Array.isArray(schedule?.siteLocationDetails)
                ? schedule.siteLocationDetails.flatMap((row) => Array.isArray(row?.siteLocationIds) ? row.siteLocationIds : [])
                : [];
              const resolvedSiteIds = (sameDateSiteIds.length > 0 ? sameDateSiteIds : fallbackSiteIds).map((x) => String(x));
              const resolvedSite = resolvedSiteIds
                .map((siteId) => siteMap.get(siteId) || null)
                .find(Boolean);
              const country = String(resolvedSite?.country || '').trim();
              const city = String(resolvedSite?.city || '').trim();
              const locationLabel = [city, country].filter(Boolean).join(', ') || siteText;

              expandedRows.push({
                id: `${projectId}-${scopeRow?.id || 'scope'}-${schedule?.id || index}-${dateKey}`,
                dateKey,
                date: toDateOnly(dateKey),
                projectId,
                title: `${project?.projectName || 'Audit Scheduling'} - ${schedule?.auditType || 'On-site'}`,
                subtitle: project?.companyName || '-',
                projectCode: project?.projectCode || `P-${projectId}`,
                certification: `Auditor: ${auditorName} | ${startDateText} - ${endDateText}`,
                extraInfo: {
                  role: schedule?.role || '-',
                  status: schedule?.scheduleStatus || '-',
                  mandays: schedule?.mandays || '-',
                  site: siteText,
                  date: dateKey,
                },
                category: {
                  id: resolvedTypeId,
                  label: resolvedTypeLabel,
                },
                timed: {
                  start: (() => {
                    const d = toDateOnly(dateKey);
                    if (!d) return null;
                    d.setHours(9, Math.min(dateIndex * 10, 50), 0, 0);
                    return d;
                  })(),
                  end: (() => {
                    const d = toDateOnly(dateKey);
                    if (!d) return null;
                    d.setHours(17, Math.min(dateIndex * 10, 50), 0, 0);
                    return d;
                  })(),
                },
              });

              if (auditorName !== '-' || employeeId) {
                assignments.push({
                  auditorKey: employeeId || `name:${auditorName}`,
                  dateKey,
                  status: normalizedStatus,
                  scheduleStatus: scheduleStatusRaw || '-',
                  title: `${project?.projectName || 'Audit Scheduling'} - ${schedule?.auditType || 'On-site'}`,
                  venue: project?.companyName || '-',
                  location: locationLabel || '-',
                  country: country || '-',
                  city: city || '-',
                  role: roleText,
                  time: `${startDateText} - ${endDateText}`,
                });
              }
            });
          });
        });
      });

      const groupedByDate = new Map();
      expandedRows.forEach((row) => {
        if (!row?.dateKey || !row?.date) return;
        const categoryId = row?.category?.id || (categoryIds.has('audit') ? 'audit' : 'category');
        const groupedKey = `${row.dateKey}::${categoryId}`;
        if (!groupedByDate.has(groupedKey)) {
          groupedByDate.set(groupedKey, []);
        }
        groupedByDate.get(groupedKey).push(row);
      });

      const nextEvents = Array.from(groupedByDate.entries()).map(([groupedKey, items]) => {
        const [dateKey] = String(groupedKey).split('::');
        const resolvedTypeId = items[0]?.category?.id || (categoryIds.has('audit') ? 'audit' : 'category');
        const resolvedTypeLabel = normalizedTypeMap[resolvedTypeId]?.label || items[0]?.category?.label || 'Schedule';
        return {
        id: `audit-${groupedKey}`,
        date: toDateOnly(dateKey),
        label: resolvedTypeLabel,
        typeId: resolvedTypeId,
        count: items.length,
        items: items.map((item, idx) => ({
          id: `${item.id}-${idx}`,
          _index: idx + 1,
          title: item.title,
          subtitle: item.subtitle,
          projectCode: item.projectCode,
          projectId: item.projectId,
          certification: `${item.certification} | Status: ${item.extraInfo.status} | Site: ${item.extraInfo.site}`,
        })),
      };
    });

      setAuditCalendarEvents(nextEvents);
      setAvailabilityAuditorPool(Array.from(auditorPoolMap.values()));
      setAvailabilityAssignments(assignments);

      // Load general schedules and merge into calendar
      try {
        const generalSchedules = await getGeneralSchedules();
        setGeneralCalendarEvents(
          convertGeneralSchedulesToCalendarEvents(
            Array.isArray(generalSchedules) ? generalSchedules : (Array.isArray(generalSchedules?.data) ? generalSchedules.data : []),
            normalizedTypeMap
          )
        );
      } catch (generalErr) {
        console.warn('Failed to load general schedules for calendar:', generalErr);
        setGeneralCalendarEvents([]);
      }

    } catch (error) {
      console.warn('Failed to load audit schedules for calendar:', error);
      setCalendarLoadError('Failed to load calendar data. Please refresh and try again.');
      setAuditCalendarEvents([]);
      setAvailabilityAuditorPool([]);
      setAvailabilityAssignments([]);
    } finally {
      setIsCalendarLoading(false);
    }
  }, [canViewPage]);

  useEffect(() => {
    if (capabilitiesLoading || !canViewPage) return;
    loadAuditCalendarEvents();
  }, [loadAuditCalendarEvents, capabilitiesLoading, canViewPage]);


  useEffect(() => {
    setTypeFilters((prev) => {
      const next = {};
      calendarEventTypes.forEach((type) => {
        next[type.id] = prev[type.id] ?? true;
      });
      return next;
    });
  }, [calendarEventTypes]);

  useEffect(() => {
    if (!addScheduleDropdownOpen) return;
    const handleClickOutside = (e) => {
      if (addScheduleRef.current && !addScheduleRef.current.contains(e.target)) {
        setAddScheduleDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [addScheduleDropdownOpen]);

  useEffect(() => {
    if (!moreDropdown.key) return;
    const handleClickOutside = (e) => {
      if (
        moreDropdownRef.current && !moreDropdownRef.current.contains(e.target) &&
        moreDropdown.anchor && !moreDropdown.anchor.contains(e.target)
      ) {
        setMoreDropdown({ key: null, anchor: null, dayLabel: '' });
      }
    };
    const handleScroll = () => setMoreDropdown({ key: null, anchor: null, dayLabel: '' });
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [moreDropdown.key, moreDropdown.anchor]);



  const calendarDays = useMemo(
    () => getCalendarDays(current.year, current.month),
    [current.year, current.month]
  );

  const availabilityCalendarDays = useMemo(
    () => getCalendarDays(availabilityCalendarMonth.getFullYear(), availabilityCalendarMonth.getMonth()),
    [availabilityCalendarMonth]
  );

  const availabilityDailySummary = useMemo(() => {
    const selectedDateKey = formatDateKey(availabilitySelectedDate);
    const assignmentByAuditor = new Map();

    availabilityAssignments
      .filter((row) => row?.dateKey === selectedDateKey)
      .forEach((row) => {
        const prev = assignmentByAuditor.get(row.auditorKey);
        if (!prev || (prev.status !== 'confirmed' && row.status === 'confirmed')) {
          assignmentByAuditor.set(row.auditorKey, row);
        }
      });

    let available = 0;
    let confirmed = 0;
    let booked = 0;

    availabilityAuditorPool.forEach((auditor) => {
      const assignment = assignmentByAuditor.get(auditor.id);
      if (!assignment) {
        available += 1;
      } else if (assignment.status === 'confirmed') {
        confirmed += 1;
      } else {
        booked += 1;
      }
    });

    return { available, confirmed, booked };
  }, [availabilityAssignments, availabilityAuditorPool, availabilitySelectedDate]);

  const availabilityResolvedRows = useMemo(() => {
    const selectedDateKey = formatDateKey(availabilitySelectedDate);
    const assignmentByAuditor = new Map();

    availabilityAssignments
      .filter((row) => row?.dateKey === selectedDateKey)
      .forEach((row) => {
        const prev = assignmentByAuditor.get(row.auditorKey);
        if (!prev || (prev.status !== 'confirmed' && row.status === 'confirmed')) {
          assignmentByAuditor.set(row.auditorKey, row);
        }
      });

    return availabilityAuditorPool.map((auditor) => {
      const assignment = assignmentByAuditor.get(auditor.id);
      if (!assignment) {
        return {
          id: auditor.id,
          name: auditor.name,
          role: auditor.role,
          status: 'available',
          location: '-',
        };
      }

      return {
        id: auditor.id,
        name: auditor.name,
        role: assignment.role || auditor.role,
        status: assignment.status,
        location: assignment.location || '-',
        country: assignment.country || '-',
        city: assignment.city || '-',
        event: {
          title: assignment.title,
          time: assignment.time,
          venue: assignment.venue,
          location: assignment.location || '-',
        },
      };
    });
  }, [availabilityAssignments, availabilityAuditorPool, availabilitySelectedDate]);

  const availabilityCountryOptions = useMemo(() => {
    const set = new Set();
    availabilityResolvedRows.forEach((row) => {
      const country = String(row?.country || '').trim();
      if (country && country !== '-') set.add(country);
    });
    return [{ id: 'All Country', name: 'All Country' }, ...Array.from(set).sort().map((name) => ({ id: name, name }))];
  }, [availabilityResolvedRows]);

  const availabilityCityOptions = useMemo(() => {
    const set = new Set();
    availabilityResolvedRows.forEach((row) => {
      const country = String(row?.country || '').trim();
      const city = String(row?.city || '').trim();
      if (!city || city === '-') return;
      if (availabilityCountry !== 'All Country' && country !== availabilityCountry) return;
      set.add(city);
    });
    return [{ id: 'All City', name: 'All City' }, ...Array.from(set).sort().map((name) => ({ id: name, name }))];
  }, [availabilityResolvedRows, availabilityCountry]);

  useEffect(() => {
    if (availabilityCity !== 'All City') {
      const validCity = availabilityCityOptions.some((opt) => opt.id === availabilityCity);
      if (!validCity) setAvailabilityCity('All City');
    }
  }, [availabilityCity, availabilityCityOptions]);

  const availabilityAuditorsFiltered = useMemo(() => {
    const list = availabilityResolvedRows.filter((a) => {
      if (availabilitySearch.trim()) {
        const q = availabilitySearch.toLowerCase();
        if (!a.name.toLowerCase().includes(q)) return false;
      }
      if (availabilityCountry !== 'All Country' && String(a?.country || '').trim() !== availabilityCountry) return false;
      if (availabilityCity !== 'All City' && String(a?.city || '').trim() !== availabilityCity) return false;
      if (availabilityStatus !== 'All Status' && a.status !== availabilityStatus) return false;
      return true;
    });
    // Confirmed always on top, then scheduled, then available
    return [...list].sort((a, b) => {
      const orderDiff = (AVAILABILITY_STATUS_ORDER[a.status] ?? 99) - (AVAILABILITY_STATUS_ORDER[b.status] ?? 99);
      if (orderDiff !== 0) return orderDiff;
      return String(a?.name || '').localeCompare(String(b?.name || ''));
    });
  }, [availabilityResolvedRows, availabilitySearch, availabilityCountry, availabilityCity, availabilityStatus]);

  const allEvents = useMemo(() => {
    return [...auditCalendarEvents, ...generalCalendarEvents];
  }, [auditCalendarEvents, generalCalendarEvents]);

  const eventTypeById = useMemo(() => {
    const out = {};
    calendarEventTypes.forEach((type) => {
      out[type.id] = type;
    });
    return out;
  }, [calendarEventTypes]);

  const categoryCounts = useMemo(() => {
    const out = {};
    calendarEventTypes.forEach((type) => {
      out[type.id] = 0;
    });
    allEvents.forEach((event) => {
      const typeId = event?.typeId;
      if (!typeId) return;
      out[typeId] = (out[typeId] || 0) + (Number(event?.count) || 1);
    });
    return out;
  }, [allEvents, calendarEventTypes]);

  const totalTypeCount = calendarEventTypes.length;
  const activeTypeCount = useMemo(
    () => calendarEventTypes.reduce((acc, type) => acc + (typeFilters[type.id] ? 1 : 0), 0),
    [calendarEventTypes, typeFilters]
  );
  const isViewAllActive = viewAll || (totalTypeCount > 0 && activeTypeCount === totalTypeCount);

  const activeFilterLabels = useMemo(
    () => calendarEventTypes.filter((type) => typeFilters[type.id]).map((type) => type.label),
    [calendarEventTypes, typeFilters]
  );

  const filteredEvents = useMemo(
    () => allEvents.filter((ev) => isViewAllActive || typeFilters[ev.typeId]),
    [allEvents, isViewAllActive, typeFilters]
  );

  const filteredEventTotal = useMemo(
    () => filteredEvents.reduce((acc, ev) => acc + (Number(ev?.count) || 1), 0),
    [filteredEvents]
  );

  const eventsByDay = useMemo(() => {
    const map = {};
    filteredEvents.forEach((ev) => {
      if (!ev.date) return;
      const key = ev.date.toDateString();
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    });
    return map;
  }, [filteredEvents]);

  const listGrouped = useMemo(() => {
    const sorted = filteredEvents
      .filter((ev) => {
        if (!ev.date) return false;
        return ev.date instanceof Date && !Number.isNaN(ev.date.getTime());
      })
      .slice()
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    const monthMap = new Map();
    sorted.forEach((ev) => {
      const y = ev.date.getFullYear();
      const m = ev.date.getMonth();
      const monthKey = `${y}-${String(m + 1).padStart(2, '0')}`;
      const monthLabel = ev.date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      const dateKey = formatDateKey(ev.date);
      const dateLabel = ev.date.toLocaleDateString('en-US', { weekday: 'short', day: 'numeric', month: 'short' });

      if (!monthMap.has(monthKey)) {
        monthMap.set(monthKey, { monthKey, monthLabel, dayMap: new Map() });
      }
      const monthEntry = monthMap.get(monthKey);
      if (!monthEntry.dayMap.has(dateKey)) {
        monthEntry.dayMap.set(dateKey, { dateKey, dateLabel, events: [] });
      }
      monthEntry.dayMap.get(dateKey).events.push(ev);
    });

    return Array.from(monthMap.values()).map((entry) => ({
      monthKey: entry.monthKey,
      monthLabel: entry.monthLabel,
      days: Array.from(entry.dayMap.values()),
    }));
  }, [filteredEvents]);

  const weekDates = useMemo(() => getWeekDates(weekStart), [weekStart]);
  const weekEvents = useMemo(() => {
    const weekStartKey = formatDateKey(weekStart);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 6);
    const weekEndKey = formatDateKey(weekEnd);

    const auditTimed = [];

    auditCalendarEvents.forEach((ev) => {
      const dateKey = formatDateKey(ev?.date);
      if (!dateKey || dateKey < weekStartKey || dateKey > weekEndKey) return;
      const items = Array.isArray(ev?.items) ? ev.items : [];
      if (items.length === 0) return;

      items.forEach((item, idx) => {
        const start = toDateOnly(dateKey);
        if (!start) return;
        start.setHours(9, Math.min(idx * 10, 50), 0, 0);
        const end = new Date(start.getTime());
        end.setHours(17, Math.min(idx * 10, 50), 0, 0);
        auditTimed.push({
          id: `${ev.id}-${idx}`,
          date: toDateOnly(dateKey),
          label: item?.title || 'Audit Scheduling',
          typeId: ev?.typeId || 'audit',
          count: 1,
          start,
          end,
          allDay: false,
          items: [item],
        });
      });
    });

    return auditTimed;
  }, [weekStart, auditCalendarEvents]);
  const weekEventsFiltered = useMemo(() => {
    return weekEvents.filter((ev) => isViewAllActive || typeFilters[ev.typeId]);
  }, [weekEvents, typeFilters, isViewAllActive]);

  const goPrev = () => {
    if (view === 'Week') {
      const next = new Date(weekStart);
      next.setDate(next.getDate() - 7);
      setWeekStart(next);
    } else if (view === 'Day') {
      const next = new Date(selectedDate);
      next.setDate(next.getDate() - 1);
      setSelectedDate(next);
      setWeekStart(getWeekStart(next));
    } else {
      setCurrent((prev) =>
        prev.month === 0 ? { year: prev.year - 1, month: 11 } : { year: prev.year, month: prev.month - 1 }
      );
    }
  };

  const goNext = () => {
    if (view === 'Week') {
      const next = new Date(weekStart);
      next.setDate(next.getDate() + 7);
      setWeekStart(next);
    } else if (view === 'Day') {
      const next = new Date(selectedDate);
      next.setDate(next.getDate() + 1);
      setSelectedDate(next);
      setWeekStart(getWeekStart(next));
    } else {
      setCurrent((prev) =>
        prev.month === 11 ? { year: prev.year + 1, month: 0 } : { year: prev.year, month: prev.month + 1 }
      );
    }
  };

  const goToday = () => {
    const d = new Date();
    setCurrent({ year: d.getFullYear(), month: d.getMonth() });
    setWeekStart(getWeekStart(d));
    setSelectedDate(d);
  };

  const dayLabel = useMemo(
    () =>
      selectedDate.toLocaleDateString('en-US', {
        weekday: 'short',
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    [selectedDate]
  );

  const monthLabel = new Date(current.year, current.month, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const weekLabel = useMemo(() => {
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    const sameYear = weekStart.getFullYear() === end.getFullYear();
    const startStr = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' });
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: sameYear ? undefined : 'numeric' });
    return sameYear ? `${startStr} – ${endStr}, ${weekStart.getFullYear()}` : `${startStr} – ${endStr}`;
  }, [weekStart]);

  const toggleTypeFilter = (id) => {
    // Clicking a category means user wants explicit filtering, so disable View All mode.
    setViewAll(false);
    setTypeFilters((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const showOnlyType = (id) => {
    setViewAll(false);
    setTypeFilters(() => {
      const next = {};
      calendarEventTypes.forEach((type) => {
        next[type.id] = type.id === id;
      });
      return next;
    });
  };

  const handleViewAllChange = (checked) => {
    setViewAll(checked);
    if (checked) {
      setTypeFilters((prev) => {
        const next = { ...prev };
        calendarEventTypes.forEach((type) => {
          next[type.id] = true;
        });
        return next;
      });
    } else {
      // When View All is turned off, start from a clean slate.
      setTypeFilters((prev) => {
        const next = { ...prev };
        calendarEventTypes.forEach((type) => {
          next[type.id] = false;
        });
        return next;
      });
    }
  };

  const isToday = (date) => {
    const t = new Date();
    return date.getDate() === t.getDate() && date.getMonth() === t.getMonth() && date.getFullYear() === t.getFullYear();
  };

  const isSelected = (date) => {
    return (
      date.getDate() === selectedDate.getDate() &&
      date.getMonth() === selectedDate.getMonth() &&
      date.getFullYear() === selectedDate.getFullYear()
    );
  };

  const handleDateClick = (date) => {
    if (!canManageCalendarSchedules) return;
    const dateStr = formatDateKey(date);
    setClickedDate(dateStr);
    setGeneralScheduleType('meeting');
    setGeneralScheduleOpen(true);
  };

  if (capabilitiesLoading) {
    return <Loading />;
  }

  if (!canViewPage) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="container-xl">
      {/* Segmented nav outside calendar-page div, left-aligned */}
      <div className="calendar-page-header">
        <nav className="nav nav-segmented nav-2 calendar-segment-nav" role="tablist">
          <button
            type="button"
            className={`nav-link ${segmentTab === 'availability' ? 'active' : ''}`}
            role="tab"
            aria-selected={segmentTab === 'availability'}
            aria-current={segmentTab === 'availability' ? 'page' : undefined}
            tabIndex={segmentTab === 'availability' ? 0 : -1}
            onClick={() => setSegmentTab('availability')}
          >
            <UsersRound className="nav-link-icon icon-2" size={20} />
            Availability Auditor
          </button>
          <button
            type="button"
            className={`nav-link ${segmentTab === 'schedule' ? 'active' : ''}`}
            role="tab"
            aria-selected={segmentTab === 'schedule'}
            aria-current={segmentTab === 'schedule' ? 'page' : undefined}
            tabIndex={segmentTab === 'schedule' ? 0 : -1}
            onClick={() => setSegmentTab('schedule')}
          >
            <CalendarIcon className="nav-link-icon icon-2" size={20} />
            Schedule
          </button>
        </nav>
      </div>

      <div className="calendar-page">
      {isCalendarLoading ? (
        <div className="calendar-loading-shell" role="status" aria-live="polite" aria-busy="true">
          <div className="calendar-loading-hero">
            <div className="calendar-loading-dot" />
            <div className="calendar-loading-title">Loading calendar timeline...</div>
            <div className="calendar-loading-subtitle">Syncing audit schedules and auditor availability</div>
          </div>
          <div className="calendar-loading-grid">
            {Array.from({ length: 8 }).map((_, idx) => (
              <div key={idx} className="calendar-loading-card shimmer" />
            ))}
          </div>
        </div>
      ) : (
      <>
      {calendarLoadError && (
        <div className="alert alert-danger mb-3" role="alert">
          {calendarLoadError}
        </div>
      )}
      {segmentTab === 'availability' && (
        <div className="calendar-body calendar-body-availability">
          <aside className="availability-left">
            <div className="availability-date-card">
              <CalendarIcon className="availability-date-icon" size={24} />
              <span className="availability-date-label">Selected Date</span>
              <span className="availability-date-value">
                {availabilitySelectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
              </span>
            </div>
            <div className="availability-calendar-wrap">
              <div className="availability-calendar-header">
                <button
                  type="button"
                  className="availability-calendar-nav"
                  onClick={() => {
                    const d = new Date(availabilityCalendarMonth);
                    d.setMonth(d.getMonth() - 1);
                    setAvailabilityCalendarMonth(d);
                  }}
                  aria-label="Previous month"
                >
                  ‹
                </button>
                <span className="availability-calendar-month">
                  {availabilityCalendarMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                </span>
                <button
                  type="button"
                  className="availability-calendar-nav"
                  onClick={() => {
                    const d = new Date(availabilityCalendarMonth);
                    d.setMonth(d.getMonth() + 1);
                    setAvailabilityCalendarMonth(d);
                  }}
                  aria-label="Next month"
                >
                  ›
                </button>
              </div>
              <div className="availability-calendar-days">
                {DAYS.map((d) => (
                  <div key={d} className="availability-calendar-day-name">{d.charAt(0)}</div>
                ))}
                {availabilityCalendarDays.flat().map((cell, i) => {
                  const isSelected =
                    cell.date.getDate() === availabilitySelectedDate.getDate() &&
                    cell.date.getMonth() === availabilitySelectedDate.getMonth() &&
                    cell.date.getFullYear() === availabilitySelectedDate.getFullYear();
                  const isCurrentMonth = cell.isCurrentMonth;
                  return (
                    <button
                      key={i}
                      type="button"
                      className={`availability-calendar-cell ${!isCurrentMonth ? 'other-month' : ''} ${isSelected ? 'selected' : ''}`}
                      onClick={() => setAvailabilitySelectedDate(cell.date)}
                    >
                      {cell.date.getDate()}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="availability-summary">
              <div className="availability-summary-block availability-summary-available">
                <span className="availability-summary-label">Available</span>
                <span className="availability-summary-count">{availabilityDailySummary.available}</span>
              </div>
              <div className="availability-summary-block availability-summary-confirmed">
                <span className="availability-summary-label">Confirmed</span>
                <span className="availability-summary-count">{availabilityDailySummary.confirmed}</span>
              </div>
              <div className="availability-summary-block availability-summary-booked">
                <span className="availability-summary-label">Booked</span>
                <span className="availability-summary-count">{availabilityDailySummary.booked}</span>
              </div>
            </div>
          </aside>
          <main className="availability-right">
            <h1 className="availability-title">Availability Auditor</h1>
            <p className="availability-subtitle">See the list of names of auditors who can be assigned</p>
            <div className="availability-search-wrap">
              <Search className="availability-search-icon" size={18} />
              <input
                type="text"
                className="availability-search-input"
                placeholder="Search Auditor"
                value={availabilitySearch}
                onChange={(e) => setAvailabilitySearch(e.target.value)}
              />
            </div>
            <div className="availability-filters">
              <div className="availability-filter-wrap">
                <AppSelect
                  name="availabilityCountry"
                  value={availabilityCountry}
                  onChange={(_, val) => setAvailabilityCountry(val || 'All Country')}
                  options={availabilityCountryOptions}
                  placeholder="All Country"
                  valueField="id"
                  labelField="name"
                />
              </div>
              <div className="availability-filter-wrap">
                <AppSelect
                  name="availabilityCity"
                  value={availabilityCity}
                  onChange={(_, val) => setAvailabilityCity(val || 'All City')}
                  options={availabilityCityOptions}
                  placeholder="All City"
                  valueField="id"
                  labelField="name"
                />
              </div>
              <div className="availability-filter-wrap">
                <AppSelect
                  name="availabilityStatus"
                  value={availabilityStatus}
                  onChange={(_, val) => setAvailabilityStatus(val || 'All Status')}
                  options={[
                    { id: 'All Status', name: 'All Status' },
                    { id: 'confirmed', name: 'Confirmed' },
                    { id: 'available', name: 'Available' },
                    { id: 'scheduled', name: 'Scheduled' },
                  ]}
                  placeholder="All Status"
                  valueField="id"
                  labelField="name"
                />
              </div>
            </div>
            <div className="availability-auditor-list">
              {availabilityAuditorsFiltered.map((auditor) => (
                <div key={auditor.id} className={`availability-auditor-card availability-auditor-card--${auditor.status}`}>
                  <div className="availability-auditor-card-main">
                    <div className="availability-auditor-info">
                      <span className="availability-auditor-name">{auditor.name}</span>
                      <span className="availability-auditor-role">{auditor.role}</span>
                    </div>
                    <div className={`availability-auditor-status availability-auditor-status-${auditor.status} badge ${auditor.status === 'confirmed' ? 'badge bg-blue-lt' : auditor.status === 'scheduled' ? 'badge bg-red-lt' : 'badge bg-green-lt'}`}>
                      <span className="availability-auditor-status-dot" />
                      {auditor.status === 'confirmed' ? 'Confirmed' : auditor.status === 'scheduled' ? 'Scheduled' : 'Available'}
                    </div>
                  </div>
                  {(auditor.status === 'confirmed' || auditor.status === 'scheduled') && auditor.event && (
                    <div className="availability-auditor-event">
                      <div className="availability-auditor-event-row">
                        <CalendarIcon className="text-black" size={16} />
                        <span className="text-black">{auditor.event.title}</span>
                      </div>
                      <div className="availability-auditor-event-row">
                        <Clock size={16} />
                        <span>{auditor.event.time}</span>
                      </div>
                      <div className="availability-auditor-event-row">
                        <Building2 size={16} />
                        <span>{auditor.event.venue}</span>
                      </div>
                      <div className="availability-auditor-event-row">
                        <MapPin size={16} />
                        <span>{auditor.event.location}</span>
                      </div>
                    </div>
                  )}
                  {auditor.status === 'available' && (
                    <div className="availability-auditor-location">
                      <MapPin size={16} />
                      <span>{auditor.location}</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </main>
        </div>
      )}

      {segmentTab === 'schedule' && (
      <div className="calendar-body">
        {/* Left sidebar */}
        <aside className="calendar-sidebar">
          {canShowAddSchedule ? (
          <div className="calendar-add-schedule-wrap" ref={addScheduleRef}>
            <button
              type="button"
              className="btn btn-primary calendar-add-schedule-btn"
              onClick={() => setAddScheduleDropdownOpen((v) => !v)}
              aria-expanded={addScheduleDropdownOpen}
              aria-haspopup="true"
            >
              <PlusIcon className="icon icon-tabler" />
              Add Schedule
              <ChevronDownIcon className={`icon icon-tabler ms-2 ${addScheduleDropdownOpen ? 'open' : ''}`} />
            </button>
            {addScheduleDropdownOpen && (
              <div className="calendar-add-schedule-dropdown" role="menu">
                {addScheduleOptionsVisible.map((opt) => {
                  const type = eventTypeById[opt.id];
                  return (
                    <button
                      key={opt.id}
                      type="button"
                      role="menuitem"
                      className="calendar-add-schedule-item"
                      style={{ '--item-color': type?.color }}
                      onClick={() => {
                        setAddScheduleDropdownOpen(false);
                        setGeneralScheduleType(opt.id);
                        setGeneralScheduleOpen(true);
                      }}
                    >
                      <span className="calendar-add-schedule-item-dot" />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          ) : null}

          <div className="calendar-mini">
            <div className="calendar-mini-header">
              <button type="button" className="calendar-mini-nav" onClick={goPrev} aria-label="Previous month">
                ‹
              </button>
              <span>{monthLabel}</span>
              <button type="button" className="calendar-mini-nav" onClick={goNext} aria-label="Next month">
                ›
              </button>
            </div>
            <div className="calendar-mini-days">
              {DAYS.map((d) => (
                <div key={d} className="calendar-mini-day-name">{d.charAt(0)}</div>
              ))}
              {getCalendarDays(current.year, current.month).flat().map((cell, i) => (
                <button
                  key={i}
                  type="button"
                  className={`calendar-mini-cell ${!cell.isCurrentMonth ? 'other-month' : ''} ${isSelected(cell.date) ? 'selected' : ''} ${isToday(cell.date) ? 'today' : ''}`}
                  onClick={() => {
                    setSelectedDate(cell.date);
                    if (canManageCalendarSchedules) handleDateClick(cell.date);
                  }}
                  title={canManageCalendarSchedules ? 'Click to add schedule on this date' : 'Select date'}
                >
                  {cell.date.getDate()}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            className="calendar-download-btn"
            disabled={!canExportCalendar}
            title={canExportCalendar ? 'Download' : 'You do not have permission to export'}
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
            Download
          </button>

          <div className="calendar-filter">
            <button type="button" className="calendar-filter-toggle" onClick={() => setFilterOpen(!filterOpen)}>
              Filter
              <svg className={filterOpen ? 'open' : ''} xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </button>
            {filterOpen && (
              <div className="calendar-filter-content">
                <div className="calendar-filter-section">
                  <span className="calendar-filter-section-title">Calendar</span>
                  <label className="calendar-filter-check">
                    <input type="checkbox" checked={isViewAllActive} onChange={(e) => handleViewAllChange(e.target.checked)} />
                    <span>View All</span>
                    <span className="count">{filteredEventTotal}</span>
                  </label>
                  <div className="calendar-filter-meta">
                    {isViewAllActive
                      ? `Showing all categories (${totalTypeCount})`
                      : `Showing ${activeTypeCount}/${totalTypeCount} categories`}
                  </div>
                  {!isViewAllActive && activeFilterLabels.length > 0 && (
                    <div className="calendar-filter-active-labels" title={activeFilterLabels.join(', ')}>
                      {activeFilterLabels.join(', ')}
                    </div>
                  )}
                  {calendarEventTypes.map((t) => (
                    <label key={t.id} className={`calendar-filter-check ${typeFilters[t.id] ? 'is-active' : ''}`} style={{ '--color': t.color }}>
                      <input type="checkbox" checked={typeFilters[t.id]} onChange={() => toggleTypeFilter(t.id)} />
                      <span>{t.label}</span>
                      <button
                        type="button"
                        className="calendar-filter-only-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          showOnlyType(t.id);
                        }}
                      >
                        Only
                      </button>
                      <span className="count">{categoryCounts[t.id] || 0}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </aside>

        {/* Main content */}
        <main className="calendar-main">
          <div className="calendar-toolbar">
            <div className="calendar-toolbar-left">
              <button type="button" className="btn btn-outline-primary" onClick={goToday}>
                Today
              </button>
              <div className="calendar-month-nav">
                <button type="button" onClick={goPrev} aria-label={view === 'Week' ? 'Previous week' : view === 'Day' ? 'Previous day' : 'Previous month'}>‹</button>
                <span className="calendar-month-label">
                  {view === 'Week' ? weekLabel : view === 'Day' ? dayLabel : monthLabel}
                </span>
                <button type="button" onClick={goNext} aria-label={view === 'Week' ? 'Next week' : view === 'Day' ? 'Next day' : 'Next month'}>›</button>
              </div>
            </div>
            <div className="calendar-view-tabs">
              {VIEWS.map((v) => (
                <button
                  key={v}
                  type="button"
                  className={`calendar-view-tab ${view === v ? 'active' : ''}`}
                  onClick={() => {
                    setView(v);
                    if (v === 'Week') setWeekStart(getWeekStart(selectedDate));
                    if (v === 'Day') setWeekStart(getWeekStart(selectedDate));
                  }}
                >
                  {v}
                </button>
              ))}
            </div>
          </div>

          {view === 'Week' && (
            <div className="calendar-week-view">
              <div className="calendar-week-header">
                <div className="calendar-week-time-col" aria-hidden="true" />
                {weekDates.map((d) => (
                  <div
                    key={d.toDateString()}
                    className={`calendar-week-day-col ${isToday(d) ? 'today' : ''}`}
                  >
                    <span className="calendar-week-day-name">{DAYS[d.getDay()]}</span>
                    <span className="calendar-week-day-num">{d.getDate()}</span>
                  </div>
                ))}
              </div>
              <div className="calendar-week-body">
                <div className="calendar-week-time-col">
                  {Array.from({ length: HOURS_PER_DAY }, (_, h) => (
                    <div key={h} className="calendar-week-time-slot">
                      <span className="calendar-week-time-label">
                        {h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="calendar-week-grid">
                  {weekDates.map((date) => (
                    <div
                      key={date.toDateString()}
                      className={`calendar-week-day-col calendar-week-day-grid ${isToday(date) ? 'today' : ''}`}
                    >
                      {Array.from({ length: TOTAL_SLOTS }, (_, i) => (
                        <div key={i} className="calendar-week-slot" />
                      ))}
                    </div>
                  ))}
                </div>
                {/* Full-width 30-min separators from time col to day grid */}
                <div className="calendar-week-separators" aria-hidden="true" />
                <div className="calendar-week-events-layer">
                  {weekDates.map((date, dayIndex) => {
                    const dayEvents = weekEventsFiltered.filter(
                      (e) => !e.allDay && e.start && e.date.toDateString() === date.toDateString()
                    );
                    return dayEvents.map((ev) => {
                      const type = eventTypeById[ev.typeId];
                      const startMinutes = ev.start.getHours() * 60 + ev.start.getMinutes();
                      const endMinutes = ev.end.getHours() * 60 + ev.end.getMinutes();
                      const topPct = (startMinutes / (HOURS_PER_DAY * 60)) * 100;
                      const heightPct = ((endMinutes - startMinutes) / (HOURS_PER_DAY * 60)) * 100;
                      return (
                        <div
                          key={ev.id}
                          className="calendar-week-event calendar-week-event-timed"
                          role="button"
                          tabIndex={0}
                          style={{
                            left: `calc(${(dayIndex / 7) * 100}% + 2px)`,
                            width: 'calc(100% / 7 - 4px)',
                            top: `calc(${topPct}% + 1px)`,
                            height: `calc(${heightPct}% - 2px)`,
                            backgroundColor: type?.bg,
                            borderLeftColor: type?.color,
                          }}
                          onClick={() => setEventDetailModal({ event: ev, date })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setEventDetailModal({ event: ev, date });
                            }
                          }}
                        >
                          <span className="calendar-week-event-title">{ev.label}</span>
                          <span className="calendar-week-event-time">
                            {ev.start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            –{ev.end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          </span>
                        </div>
                      );
                    });
                  })}
                </div>
                <div className="calendar-week-now-line-wrapper">
                  {(() => {
                    const now = new Date();
                    const todayIndex = weekDates.findIndex((d) => d.toDateString() === now.toDateString());
                    if (todayIndex === -1) return null;
                    const nowMinutes = now.getHours() * 60 + now.getMinutes();
                    const nowTopPct = (nowMinutes / (HOURS_PER_DAY * 60)) * 100;
                    return (
                      <div
                        className="calendar-week-now-line"
                        style={{
                          left: `${(todayIndex / 7) * 100}%`,
                          width: `${100 / 7}%`,
                          top: `${nowTopPct}%`,
                        }}
                        aria-hidden="true"
                      />
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {view === 'Month' && (
            <div className="calendar-grid">
              {DAYS.map((d) => (
                <div key={d} className="calendar-grid-header-cell">{d}</div>
              ))}
              {calendarDays.map((week, wi) =>
                week.map((cell, ci) => {
                  const key = `${wi}-${ci}`;
                  const dayKey = cell.date.toDateString();
                  const dayEvents = eventsByDay[dayKey] || [];
                  const maxShow = dayEvents.length > 2 ? 1 : 2;
                  const showEvents = dayEvents.slice(0, maxShow);
                  const hiddenCount = dayEvents.length - maxShow;
                  const hasMore = hiddenCount > 0;
                  const isMoreOpen = moreDropdown.key === key;

                  return (
                    <div
                      key={key}
                      className={`calendar-grid-cell ${!cell.isCurrentMonth ? 'other-month' : ''} ${isToday(cell.date) ? 'today' : ''}`}
                    >
                      <div className="calendar-cell-date-row">
                        {canManageCalendarSchedules ? (
                        <button
                          type="button"
                          className="calendar-cell-add-btn"
                          onClick={(e) => { e.stopPropagation(); handleDateClick(cell.date); }}
                          aria-label={`Add schedule on ${cell.date.toLocaleDateString()}`}
                          title="Add schedule"
                        >+</button>
                        ) : null}
                        <span className="calendar-cell-date">{cell.date.getDate()}</span>
                      </div>
                      <div className="calendar-cell-events">
                        <div className="calendar-cell-events-list">
                          {showEvents.map((ev) => {
                            const type = eventTypeById[ev.typeId];
                            return (
                              <button
                                key={ev.id}
                                type="button"
                                className="calendar-cell-event"
                                style={{ backgroundColor: type?.bg, borderLeftColor: type?.color }}
                                onClick={() => setEventDetailModal({ event: ev, date: cell.date })}
                              >
                                <span className="event-label">{ev.label}</span>
                                <span className="event-count" style={{ backgroundColor: type?.bg, color: type?.color }}>
                                  {ev.count}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                        {hasMore && (
                          <div className="calendar-cell-more-wrap">
                            <button
                              type="button"
                              className="calendar-cell-view-more"
                              onClick={(e) => {
                                e.stopPropagation();
                                setMoreDropdown({
                                  key: isMoreOpen ? null : key,
                                  anchor: isMoreOpen ? null : e.currentTarget,
                                  dayLabel: cell.date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
                                });
                              }}
                              aria-expanded={isMoreOpen}
                              aria-haspopup="true"
                            >
                              +{hiddenCount} more
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          )}

          {/* Portal: dropdown for hidden events (Outlook-style +N more) */}
          {moreDropdown.key && moreDropdown.anchor && typeof document !== 'undefined' && (() => {
            const [wi, ci] = moreDropdown.key.split('-').map(Number);
            const cell = calendarDays[wi]?.[ci];
            if (!cell) return null;
            const dayKey = cell.date.toDateString();
            const dayEvents = eventsByDay[dayKey] || [];
            const maxShow = dayEvents.length > 2 ? 1 : 2;
            const hiddenEvents = dayEvents.slice(maxShow);
            if (hiddenEvents.length === 0) return null;
            const rect = moreDropdown.anchor.getBoundingClientRect();
            return ReactDOM.createPortal(
              <div
                ref={moreDropdownRef}
                className="calendar-more-dropdown"
                style={{
                  left: Math.min(rect.left, window.innerWidth - 284),
                  top: rect.bottom + 4,
                }}
                role="menu"
              >
                <div className="calendar-more-dropdown-title">{moreDropdown.dayLabel}</div>
                {hiddenEvents.map((ev) => {
                  const type = eventTypeById[ev.typeId];
                  return (
                    <button
                      key={ev.id}
                      type="button"
                      className="calendar-more-dropdown-item"
                      role="menuitem"
                      style={{ borderLeftColor: type?.color }}
                      onClick={() => {
                      setMoreDropdown({ key: null, anchor: null, dayLabel: '' });
                      setEventDetailModal({ event: ev, date: cell.date });
                    }}
                    >
                      <span className="event-label">{ev.label}</span>
                      <span className="event-count" style={{ backgroundColor: type?.bg, color: type?.color }}>
                        {ev.count}
                      </span>
                    </button>
                  );
                })}
              </div>,
              document.body
            );
          })()}

          {/* Event detail modal: list of items (e.g. audit schedules) with project data */}
          {eventDetailModal && (() => {
            const { event, date } = eventDetailModal;
            const type = eventTypeById[event.typeId];
            const items = getEventDetailItems(event);
            const dateStr = date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
            return (
              <div
                className="calendar-event-modal-backdrop"
                onClick={() => setEventDetailModal(null)}
                onKeyDown={(e) => e.key === 'Escape' && setEventDetailModal(null)}
                role="dialog"
                aria-modal="true"
                aria-labelledby="calendarEventModalTitle"
              >
                <div
                  className="calendar-event-modal"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="calendar-event-modal-header">
                    <div className="calendar-event-modal-eyebrow">
                      <span className="calendar-event-modal-title-dot" />
                      {type?.label || event.label}
                    </div>
                    <h2 id="calendarEventModalTitle" className="calendar-event-modal-title">
                      {event.label}
                    </h2>
                    <p className="calendar-event-modal-subtitle">{dateStr}</p>
                    <div className="calendar-event-modal-header-meta">
                      <span className="calendar-event-modal-status">{event.count} scheduled item{event.count !== 1 ? 's' : ''}</span>
                      {event.typeId === 'audit' && <span className="calendar-event-modal-projects">Audit schedule</span>}
                    </div>
                    <button
                      type="button"
                      className="calendar-event-modal-close"
                      onClick={() => setEventDetailModal(null)}
                      aria-label="Close"
                    >
                      <X size={18} aria-hidden="true" />
                    </button>
                  </div>
                  <div className="calendar-event-modal-body">
                    <ul className="calendar-event-modal-list">
                      {items.map((item) => (
                        <li key={item.id} className="calendar-event-modal-list-item">
                          <div className="calendar-event-modal-item-topline">
                            <span className="calendar-event-modal-item-num">{String(item._index).padStart(2, '0')}</span>
                            <div className="min-w-0 flex-grow-1">
                              <div className="calendar-event-modal-item-row">
                                <span className="calendar-event-modal-item-title">{item.title}</span>
                                {event.typeId === 'audit' && <span className="calendar-event-modal-item-status">Booked</span>}
                              </div>
                              <div className="calendar-event-modal-item-meta">
                                <span className="calendar-event-modal-item-company">{item.subtitle}</span>
                                {item.projectCode && item.projectCode !== '—' && (
                                  <span className="calendar-event-modal-item-code">{item.projectCode}</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="calendar-event-modal-details">
                            {item.certification && item.certification !== '—' && <span>Auditor · {item.certification}</span>}
                            {item.location && <span>Location · {item.location}</span>}
                          </div>
                          <div className="calendar-event-modal-item-actions">
                            {event.typeId === 'audit' && item.projectId && (
                              <button
                                type="button"
                                className="calendar-event-modal-process-link"
                                onClick={() => {
                                  setEventDetailModal(null);
                                  navigate(`/audit-process/${item.projectId}`);
                                }}
                              >
                                View Audit Process <ArrowRight size={15} aria-hidden="true" />
                              </button>
                            )}
                          {canManageCalendarSchedules && event.typeId !== 'audit' && item.scheduleId && (
                              <button
                                type="button"
                                className="calendar-event-modal-edit-link"
                                onClick={() => {
                                  setEditGeneralScheduleData(item.rawSchedule || { id: item.scheduleId });
                                  setGeneralScheduleType(event.typeId || 'meeting');
                                  setGeneralScheduleOpen(true);
                                  setEventDetailModal(null);
                                }}
                              >
                                Edit / Reschedule
                              </button>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            );
          })()}

          {view === 'Day' && (
            <div className="calendar-week-view calendar-day-view">
              <div className="calendar-week-header">
                <div className="calendar-week-time-col" aria-hidden="true" />
                <div className={`calendar-week-day-col ${isToday(selectedDate) ? 'today' : ''}`}>
                  <span className="calendar-week-day-name">{DAYS[selectedDate.getDay()]}</span>
                  <span className="calendar-week-day-num">{selectedDate.getDate()}</span>
                </div>
              </div>
              <div className="calendar-week-body">
                <div className="calendar-week-time-col">
                  {Array.from({ length: HOURS_PER_DAY }, (_, h) => (
                    <div key={h} className="calendar-week-time-slot">
                      <span className="calendar-week-time-label">
                        {h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="calendar-week-grid">
                  <div className={`calendar-week-day-col calendar-week-day-grid ${isToday(selectedDate) ? 'today' : ''}`}>
                    {Array.from({ length: TOTAL_SLOTS }, (_, i) => (
                      <div key={i} className="calendar-week-slot" />
                    ))}
                  </div>
                </div>
                <div className="calendar-week-separators" aria-hidden="true" />
                <div className="calendar-week-events-layer">
                  {weekEventsFiltered
                    .filter((e) => !e.allDay && e.start && e.date.toDateString() === selectedDate.toDateString())
                    .map((ev) => {
                      const type = eventTypeById[ev.typeId];
                      const startMinutes = ev.start.getHours() * 60 + ev.start.getMinutes();
                      const endMinutes = ev.end.getHours() * 60 + ev.end.getMinutes();
                      const topPct = (startMinutes / (HOURS_PER_DAY * 60)) * 100;
                      const heightPct = ((endMinutes - startMinutes) / (HOURS_PER_DAY * 60)) * 100;
                      return (
                        <div
                          key={ev.id}
                          className="calendar-week-event calendar-week-event-timed"
                          role="button"
                          tabIndex={0}
                          style={{
                            left: '2px',
                            width: 'calc(100% - 4px)',
                            top: `calc(${topPct}% + 1px)`,
                            height: `calc(${heightPct}% - 2px)`,
                            backgroundColor: type?.bg,
                            borderLeftColor: type?.color,
                          }}
                          onClick={() => setEventDetailModal({ event: ev, date: selectedDate })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              setEventDetailModal({ event: ev, date: selectedDate });
                            }
                          }}
                        >
                          <span className="calendar-week-event-title">{ev.label}</span>
                          <span className="calendar-week-event-time">
                            {ev.start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            –{ev.end.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                          </span>
                        </div>
                      );
                    })}
                </div>
                <div className="calendar-week-now-line-wrapper">
                  {isToday(selectedDate) && (() => {
                    const now = new Date();
                    const nowMinutes = now.getHours() * 60 + now.getMinutes();
                    const nowTopPct = (nowMinutes / (HOURS_PER_DAY * 60)) * 100;
                    return (
                      <div
                        className="calendar-week-now-line"
                        style={{
                          left: 0,
                          width: '100%',
                          top: `${nowTopPct}%`,
                        }}
                        aria-hidden="true"
                      />
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {view === 'List' && (
            <div className="calendar-list-view">
              {listGrouped.length === 0 ? (
                <div className="calendar-list-empty">
                  <CalendarIcon size={40} className="calendar-list-empty-icon" />
                  <p className="calendar-list-empty-text">No scheduled events</p>
                </div>
              ) : (
                listGrouped.map((monthGroup) => (
                  <div key={monthGroup.monthKey} className="calendar-list-month-group">
                    <div className="calendar-list-month-header">{monthGroup.monthLabel}</div>
                    {monthGroup.days.map((dayGroup) => (
                      <div key={dayGroup.dateKey} className="calendar-list-day-group">
                        <div className="calendar-list-day-label">{dayGroup.dateLabel}</div>
                        <div className="calendar-list-day-events">
                          {dayGroup.events.map((ev) => {
                            const type = eventTypeById[ev.typeId];
                            return (
                              <button
                                key={ev.id}
                                type="button"
                                className="calendar-list-event-row"
                                style={{ '--ev-color': type?.color, '--ev-bg': type?.bg }}
                                onClick={() => setEventDetailModal({ event: ev, date: ev.date })}
                              >
                                <span className="calendar-list-event-dot" />
                                <span className="calendar-list-event-label">{ev.label}</span>
                                <span className="calendar-list-event-title">{ev.items?.[0]?.title || ev.label}</span>
                                {ev.count > 1 && (
                                  <span className="calendar-list-event-count">{ev.count} items</span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                ))
              )}
            </div>
          )}
        </main>
      </div>
      )}
      </>
      )}
      </div>

      {canManageCalendarSchedules ? (
      <AddGeneralScheduleOffcanvas
        isOpen={generalScheduleOpen}
        scheduleType={generalScheduleType}
        initialDate={clickedDate}
        editData={editGeneralScheduleData}
        allowAuditTypeSwitch={false}
        onScheduleSaved={() => {
          setGeneralScheduleOpen(false);
          setEditGeneralScheduleData(null);
          setClickedDate('');
          loadAuditCalendarEvents();
        }}
        onClose={() => {
          setGeneralScheduleOpen(false);
          setEditGeneralScheduleData(null);
          setClickedDate('');
        }}
      />
      ) : null}
    </div>
  );
};

export default Calendar;
